import { motion } from "motion/react";
import { invoiceDocument, isOverdue, type Dataset, type Invoice } from "@otrack/domain";
import { Icon } from "@otrack/ui";
import { money } from "./components";
import { href } from "./router";
import { formatPeriod, formatShortDate } from "./time";

export type InvoiceState = "draft" | "sent" | "overdue" | "paid";

export function invoiceState(invoice: Invoice, now: number): InvoiceState {
  if (invoice.status === "draft") return "draft";
  if (invoice.status === "paid") return "paid";
  return isOverdue(invoice, now) ? "overdue" : "sent";
}

const STATE_LABEL: Record<InvoiceState, string> = { draft: "Draft", sent: "Sent", overdue: "Overdue", paid: "Paid" };

export function InvoiceStateTag({ state }: { state: InvoiceState }) {
  return <span className={`ot-istate is-${state}`}>{STATE_LABEL[state]}</span>;
}

export const TERMS: Array<{ value: string; label: string; hint?: string }> = [
  { value: "0", label: "On receipt", hint: "Net 0" },
  { value: "7", label: "Net 7", hint: "7 days" },
  { value: "15", label: "Net 15", hint: "15 days" },
  { value: "30", label: "Net 30", hint: "30 days" }
];

/** "16" or "16,5" typed as a percentage, in basis points; undefined when unreadable. */
export function parsePercent(text: string): number | undefined {
  const value = Number(text.trim().replace(",", ".").replace(/%$/, ""));
  if (!text.trim() || !Number.isFinite(value) || value < 0 || value > 100) return undefined;
  return Math.round(value * 100);
}

export const percentText = (basisPoints: number) => String(basisPoints / 100);

/** "Oct 1 - 31, 2026" in the app's English. */
export function periodLabel(invoice: Pick<Invoice, "periodFrom" | "periodTo">): string {
  return formatPeriod(invoice.periodFrom, invoice.periodTo);
}

/** When it matters next: the due date while Sent, the paid date once Paid, when it was started while Draft. */
function timing(invoice: Invoice, state: InvoiceState): string {
  if (state === "paid" && invoice.paidAt !== null) return `Paid ${formatShortDate(invoice.paidAt)}`;
  if ((state === "sent" || state === "overdue") && invoice.dueDate !== null) return `Due ${formatShortDate(invoice.dueDate)}`;
  return `Started ${formatShortDate(invoice.createdAt)}`;
}

/** Newest first: Drafts by creation, the rest by issue date. */
export function byRecent(a: Invoice, b: Invoice): number {
  return (b.issueDate ?? b.createdAt) - (a.issueDate ?? a.createdAt) || b.createdAt - a.createdAt;
}

/** Keeps a split's Installments together, in order, where the first of them falls in the list. */
export function withInstallmentsTogether(invoices: Invoice[]): Invoice[] {
  const groups = new Map<string, Invoice[]>();
  for (const invoice of invoices) if (invoice.installment) groups.set(invoice.installment.groupId, [...(groups.get(invoice.installment.groupId) ?? []), invoice]);
  return invoices.flatMap((invoice) => {
    if (!invoice.installment) return [invoice];
    const group = groups.get(invoice.installment.groupId);
    if (!group) return [];
    groups.delete(invoice.installment.groupId);
    return group.sort((a, b) => a.installment!.index - b.installment!.index);
  });
}

export function InvoiceRow({ invoice, dataset, now, showClient = true, index = 0 }: { invoice: Invoice; dataset: Dataset; now: number; showClient?: boolean; index?: number }) {
  const state = invoiceState(invoice, now);
  const client = dataset.clients[invoice.clientId];
  const { totals, installment } = invoiceDocument(dataset, invoice);
  return <motion.a
    layout="position"
    className={`ot-irow is-${state}`}
    href={href({ page: "invoice", id: invoice.id })}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: Math.min(index, 8) * 0.03 }}
  >
    <span className="ot-irow__number ot-mono">
      {invoice.number ?? "Draft"}
      {installment && <span className="ot-irow__part" title={`Installment ${installment.index} of ${installment.count}`}>{installment.index}/{installment.count}</span>}
    </span>
    <span className="ot-irow__main">
      <strong>{showClient ? client?.name ?? "Unknown client" : periodLabel(invoice)}</strong>
      <span>{showClient ? periodLabel(invoice) : invoice.reference || invoice.currency}</span>
    </span>
    <span className="ot-irow__when">{timing(invoice, state)}</span>
    <InvoiceStateTag state={state}/>
    <span className="ot-irow__amount ot-mono">{money(totals.total, invoice.currency)}</span>
    <Icon name="arrow-right" className="ot-irow__go"/>
  </motion.a>;
}
